import {
  AnalyticsEvents,
  type ByoPostCreatedProperties,
  type ByoUploadOpenedProperties,
} from '@mondaygpt/analytics-events'
import { MAX_INTENT_INGREDIENTS, type LivePlatform } from '@mondaygpt/types'
import { Button } from 'components'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import { cn } from 'lib/utils'
import {
  ArrowUp,
  Image as ImageIcon,
  Loader2,
  Plus,
  RotateCcw,
  Sparkles,
  Upload,
  Video,
  X,
} from 'lucide-react'
import { apiCall } from 'modules/core/services/api/api'
import { CreationIntentService } from 'openapi/services/CreationIntentService'
import { CreativeService } from 'openapi/services/CreativeService'
import { MediaService } from 'openapi/services/MediaService'
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ulid } from 'ulid'
import { useTopicStore } from '../store/topic-store'
import {
  INGREDIENT_MAX_IMAGE_BYTES,
  INGREDIENT_MAX_VIDEO_BYTES,
  byoAcceptAttrFor,
  byoInviteFor,
  byoMediaKind,
  byoMixError,
} from '../utils/byo-media'
import { extractApiError } from '../utils/extract-api-error'
import { HeroModeToggle } from './HeroModeToggle'
import { ShapeSlider, type IntentShape } from './ShapeSlider'

/**
 * Fallback ghost ideas for the typewriter when the topic store hasn't produced
 * business-specific suggestions yet. The moment real topics exist, the
 * typewriter types THOSE instead, and they become adoptable.
 */
const PLACEHOLDERS = [
  'We got the new fall menu in this week...',
  'Announce that we now open Sundays...',
  'Something about our 5-star review from Dana...',
  'Behind the scenes of this morning’s delivery...',
  'A carousel with 3 tips our customers always ask about...',
]

const MAX_PROMPT_LENGTH = 2000
/** Prompts shorter than this never trigger the shot-picking pass. */
const MIN_HINT_PROMPT_LENGTH = 12
/** The thinking moment narrates progress instead of freezing on one line. */
// The thinking moment TEACHES instead of narrating process (Matan, round 8):
// why real footage matters, then the no-pressure invitation. Ordered so a fast
// resolve still lands the lesson: action first, the why second, the ask last
// (and the slots card that follows repeats the reassurance at the moment of
// the ask). Lines advance every 1.3s and rest on the last one.
const THINKING_LINES = [
  'Thinking what shots this post needs…',
  'Real footage is what your audience trusts.',
  'Have them? Drop them in. If not, Taka gets close.',
]

// The thinking constellation: hand-placed, not random — a curated night sky
// that renders identically every time (no re-render jitter, no SSR mismatch).
// Denser to the right of the shimmer line; two strays drift near the text so
// the whole row participates. The first five are the mobile subset.
const THINKING_TWINKLES: Array<{ top: number; left: number; s: number; d: number; dur: number }> = [
  { top: 24, left: 56, s: 17, d: 150, dur: 2700 },
  { top: 62, left: 68, s: 11, d: 700, dur: 2200 },
  { top: 16, left: 82, s: 13, d: 400, dur: 2500 },
  { top: 66, left: 90, s: 9, d: 1100, dur: 1900 },
  { top: 40, left: 37, s: 8, d: 900, dur: 2400 },
  { top: 70, left: 48, s: 10, d: 300, dur: 2100 },
  { top: 12, left: 65, s: 8, d: 1300, dur: 2000 },
  { top: 44, left: 76, s: 15, d: 0, dur: 2600 },
  { top: 20, left: 95, s: 10, d: 850, dur: 2300 },
]

/** One source for the clip-honesty note (it renders in two shelf layouts). */
const CLIP_NOTE =
  "Your clip's best frame becomes part of the post. Using its motion is coming soon."

/** Drag drop-target treatments, shared by every frame/tile in the shelf. */
const DROP_TARGET_CLS =
  'border-acid bg-acid/15 scale-[1.05] border-solid shadow-[0_0_18px_rgba(198,255,61,0.3)]'
const DROP_SIBLING_CLS = 'border-acid/40 bg-acid/5'

// The idle tray's cluster of blank frames: photo frames waiting to be filled,
// overlapping and one tilted so the pile reads hand-placed, not grid-placed
// (Matan, round 4: the drop row must "look like a place to drop ingredients").
// The tilted frame renders last (on top) with a solid card face so its tilt
// occludes the frames beneath instead of crosshatching their dashes.
/**
 * The idle ingredient pill's media stack: a photo card in front, a video card
 * peeking behind, tilted so the pile reads hand-placed (Matan, round 7: a
 * stack of images and videos, soft, NOT in the accent color; acid appears
 * only at the drag moment).
 */
const IDLE_STACK: Array<{ cls: string; restTilt: string; icon: 'image' | 'video' }> = [
  {
    cls: 'left-0 top-[3px] h-[26px] w-[26px] opacity-80',
    restTilt: 'rotate-[-6deg]',
    icon: 'video',
  },
  { cls: 'left-[15px] top-0 h-[30px] w-[30px]', restTilt: 'rotate-[5deg]', icon: 'image' },
]

// The product-reveal post type asked in words (server: detectCarouselArc). Its one photo is
// the deck, so the ask is a carousel whatever the photo count.
const REVEAL_ASK_RE =
  /\b(product[- ]reveal|reveal (?:carousel|deck|post|series)|the big reveal|(?:torn|ripped)[- ]paper (?:reveal|carousel|peek|tear|effect|post|style|deck)|hold the dots|(?:tease|peek)\b[^.!?]{0,40}\bthen (?:the )?reveal|reveal(?:s|ed|ing)? (?:it|them|the product|our (?:new )?\w+|my (?:new )?\w+) (?:as|when|while) (?:people|they|you|everyone) (?:swipe|scroll))/i

/** Client mirror of the server's shape decision, for the live Auto label. */
function inferShape(prompt: string, readyPhotos: number): IntentShape {
  if (REVEAL_ASK_RE.test(prompt) && !/\b(videos?|reels?|clips?|footage)\b/i.test(prompt))
    return 'carousel'
  if (readyPhotos >= 2) return 'carousel'
  if (readyPhotos === 1) return 'static_image'
  if (/\b(videos?|reels?|clips?|footage)\b/i.test(prompt)) return 'short_vertical_video'
  // The hook video post type asked without the word "video" (server: detectVideoFormat).
  if (
    /\b((?:value|answer|answers|details|the rest|tips|steps|points) (?:is|are|live|lives|go|goes) in the caption|read the caption|caption (?:post|reel))\b/i.test(
      prompt
    )
  )
    return 'short_vertical_video'
  if (
    /\b(carousel|slides?|steps|tips|reasons|ways|myths?|faqs?|q\s*(?:&|and)\s*as?|how[- ]to|how do i|step[- ]by[- ]step|steps to|tutorial|walkthrough|guide to)\b/i.test(
      prompt
    )
  )
    return 'carousel'
  return 'static_image'
}
const MAX_INGREDIENTS = MAX_INTENT_INGREDIENTS
const MAX_INGREDIENT_BYTES = INGREDIENT_MAX_IMAGE_BYTES
const MAX_VIDEO_BYTES = INGREDIENT_MAX_VIDEO_BYTES

interface Ingredient {
  localId: string
  file: File
  previewUrl: string
  kind: 'image' | 'video'
  status: 'uploading' | 'ready' | 'error'
  attachmentId?: string
}

/** What the ghost overlay is currently offering, for adoption. */
export interface GhostPick {
  text: string
  topicId: string | null
  idx: number
}

export interface GhostOverlayHandle {
  /** The idea currently on screen, or null while nothing is adoptable. */
  current: () => GhostPick | null
}

/**
 * The ghost typewriter lives in its own memoized child: it re-renders every
 * 34ms while Home sits idle, and isolating it here keeps that churn off the
 * ~1100-line hero subtree. Owns typing/holding/exiting and the inline
 * "Use this idea" pill; adoption is delegated up (the parent owns the prompt).
 */
const GhostOverlay = memo(
  forwardRef<
    GhostOverlayHandle,
    {
      active: boolean
      suggestions: { id: string; headline: string }[]
      submitting: boolean
      isMobile: boolean
      onAdopt: (pick: GhostPick) => void
    }
  >(function GhostOverlay({ active, suggestions, submitting, isMobile, onAdopt }, ref) {
    const [typed, setTyped] = useState('')
    // typing → holding (idea complete, pill lands) → exiting (dissolve) → next.
    const [phase, setPhase] = useState<'typing' | 'holding' | 'exiting'>('typing')
    const [ideaIdx, setIdeaIdx] = useState(0)
    // Real business ideas when they exist; generic examples are only the
    // cold-start filler (and aren't adoptable).
    const ideas = useMemo(
      () => (suggestions.length ? suggestions.map(s => s.headline) : PLACEHOLDERS),
      [suggestions]
    )

    useEffect(() => {
      if (!active) return
      const full = ideas[ideaIdx % ideas.length]!
      // A restart (topic list changed under us) must not leave the previous
      // list's text on screen — adopt would grab a different idea than shown.
      setTyped('')
      setPhase('typing')
      let charIndex = 0
      const tick = () => {
        charIndex += 1
        setTyped(full.slice(0, charIndex))
        if (charIndex >= full.length) {
          setPhase('holding')
          timer = window.setTimeout(() => {
            // Dissolve instead of vanishing: the text blurs and lifts away,
            // then the next idea starts typing.
            setPhase('exiting')
            timer = window.setTimeout(() => setIdeaIdx(i => i + 1), 480)
          }, 3000)
          return
        }
        timer = window.setTimeout(tick, 34)
      }
      let timer = window.setTimeout(tick, 400)
      return () => clearTimeout(timer)
    }, [active, ideas, ideaIdx])

    const pickable = useCallback((): GhostPick | null => {
      // During the exit dissolve ideaIdx may already point at the NEXT idea
      // while the old text is still fading; adopting an idea that was never
      // shown would betray the click.
      if (!suggestions.length || phase === 'exiting' || !typed) return null
      const idx = ideaIdx % suggestions.length
      const picked = suggestions[idx]!
      return { text: picked.headline, topicId: picked.id, idx }
    }, [suggestions, phase, typed, ideaIdx])
    useImperativeHandle(ref, () => ({ current: pickable }), [pickable])

    if (!active) return null
    return (
      <div className="pointer-events-none absolute inset-x-5 top-5 text-[18px] leading-snug font-medium md:text-[20px]">
        <span
          aria-hidden="true"
          className={cn(
            // opacity keeps the ghost in the SUGGESTION register: at 18-20px
            // medium, plain paper-faint read as already-entered input, and
            // users pressed a Create button that (correctly) did nothing.
            'text-paper-faint opacity-70 transition-all duration-300',
            phase === 'exiting' && 'translate-y-1 opacity-0 blur-[3px]'
          )}
        >
          {typed}
          {typed && phase !== 'exiting' && (
            <span className="animate-caret ml-0.5 inline-block h-[1.05em] w-[2px] bg-acid align-text-bottom" />
          )}
        </span>
        {(phase === 'holding' || phase === 'exiting') && suggestions.length > 0 && (
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              const pick = pickable()
              if (pick) onAdopt(pick)
            }}
            aria-label={`Use this idea: ${ideas[ideaIdx % ideas.length]}`}
            className={cn(
              'pointer-events-auto ml-2.5 inline-flex -translate-y-[2px] items-center gap-1.5 rounded-full border border-acid/50 bg-bg-2/80 px-2.5 py-1 align-middle text-[11.5px] text-paper shadow-[0_0_14px_rgba(198,255,61,0.18)] transition-colors hover:border-acid disabled:opacity-50',
              // Gentle in (600ms ease-out, a 150ms beat after the idea
              // completes), gentle out (450ms keyframe with the dissolve).
              phase === 'holding' ? 'hero-pill-in' : 'hero-pill-out'
            )}
          >
            <Sparkles size={12} className="flex-shrink-0 text-acid" />
            Use this idea
            {!isMobile && (
              <kbd className="rounded-[4px] border border-line px-1 text-[10px] text-paper-faint">
                Tab
              </kbd>
            )}
          </button>
        )}
      </div>
    )
  })
)

/**
 * The creation moment's "Tell me" + "Show me" doors: the Home hero prompt and
 * the ingredient shelf. Typing an idea, dropping photos, or both converge on
 * one creation intent — one opinionated post — and navigate straight to the
 * /post/:id arrival view while generation streams in the background. Dropped
 * photos BECOME the post's imagery (1 photo → static built on it, 2+ → a
 * carousel they lead). Design: playbook/product/home-creation-moment/DESIGN.md.
 */
export interface HeroSeed {
  text: string
  /** Monotonic key so re-picking the same card re-applies the seed. */
  key: number
}

export function CreationHero({ streamSeed }: { streamSeed?: HeroSeed | null }) {
  const { track } = useAnalytics()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [prompt, setPrompt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Ref mirror so stable callbacks (remove/retry) can check without re-binding.
  const submittingRef = useRef(false)
  submittingRef.current = submitting
  const ghostRef = useRef<GhostOverlayHandle>(null)
  const [ingredients, setIngredients] = useState<Ingredient[]>([])
  const [dragOver, setDragOver] = useState(false)
  // null = Auto (Taka decides). The slider makes the override explicit while
  // keeping Auto the visible default — never demanded upfront.
  const [shapeOverride, setShapeOverride] = useState<IntentShape | null>(null)
  // Bring-your-own-content (a finished post published as-is, no AI touch on
  // pixels, 0 credits). The intent is declared UPFRONT at the card level
  // (Matan, round 10: the on-media toggle was "too difficult and
  // unexpected"): two visible rooms, Create with Taka (generation) and Post
  // your own (as-is publishing). Media and typed words carry across the
  // switch, so picking the "wrong" room first never loses work. Post as-is
  // creates the post directly and lands on the arrival PostModal for
  // platforms + time — the dedicated upload modal is gone (DESIGN.md).
  const [heroMode, setHeroMode] = useState<'create' | 'own'>('create')
  const [byoError, setByoError] = useState<string | null>(null)
  // Post-your-own format: before files it's the user's DECLARED intent
  // (filters the picker, retells the dropzone); after files the media's
  // derived shape takes the thumb (fact over intent) and a mismatched drop
  // simply wins — forgiving, no error ceremony (Matan, round 11).
  const [byoChoice, setByoChoice] = useState<IntentShape | null>(null)
  // A pruned/added tile changes the mix; stale rule errors must not linger.
  useEffect(() => {
    setByoError(null)
  }, [ingredients])
  // True inside the Post-your-own room. Unlike the old shelf toggle it does
  // NOT require media: the empty room is a dropzone invite, which is the
  // discoverable door users like Jamie arrive looking for.
  const readyMode = heroMode === 'own'
  // Counter, not boolean: dragleave fires on every CHILD boundary crossing
  // (textarea, chips, footer), which would flicker the drop highlight off
  // while the user is still dragging over the box.
  const dragDepthRef = useRef(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Tracks where the current prompt text was seeded from (ghost idea / stream
  // card) so the submit event attributes the right source. Compared against
  // the live prompt at submit time — an edited seed counts as hero_prompt.
  const seededRef = useRef<{ text: string; source: 'chip' | 'stream'; topicId?: string } | null>(
    null
  )
  // Interval driving the ghost idea's self-typing adoption; anything that
  // takes over the prompt (keystroke, stream pick, submit) must clear it.
  const adoptAnimRef = useRef<number | null>(null)

  // Prompt-aware ingredient hints: once an idea is typed, Taka visibly picks
  // the SPECIFIC shots that would make it real ("The new menu, up close").
  // While it picks, the card shows a thinking state and Create post stays
  // disabled — the ask must land before the user decides to skip it.
  const [hints, setHints] = useState<string[]>([])
  const [thinking, setThinking] = useState(false)
  const thinkingRef = useRef(false)
  thinkingRef.current = thinking
  // Keyed per prompt (not a single slot): a slow response for prompt A must
  // not clobber the cached hints for prompt B the user has since typed.
  const hintsCacheRef = useRef<Map<string, string[]>>(new Map())
  const cacheHints = useCallback((text: string, value: string[]) => {
    const cache = hintsCacheRef.current
    cache.delete(text)
    cache.set(text, value)
    if (cache.size > 12) {
      const oldest = cache.keys().next().value
      if (oldest !== undefined) cache.delete(oldest)
    }
  }, [])
  // One request per prompt even across effect restarts (a trailing space
  // mid-flight re-runs the effect with the same trimmed text; the new run
  // attaches to the in-flight promise instead of paying the LLM twice).
  const hintsInflightRef = useRef<{ prompt: string; promise: Promise<string[]> } | null>(null)
  useEffect(() => {
    const text = prompt.trim()
    // Post-your-own room: the field is a CAPTION, not an idea — a shot-hints
    // pass on it would be invisible LLM spend for frames that never render.
    if (readyMode) {
      setThinking(false)
      return
    }
    if (text.length < MIN_HINT_PROMPT_LENGTH) {
      setHints([])
      setThinking(false)
      return
    }
    // No fetch while photos already fill the shelf: the hint frames aren't
    // rendered, so the call would be invisible spend.
    if (submitting) return
    if (ingredients.length > 0) {
      setThinking(false)
      // The shelf keeps the asked-for shots while their prompt stands, but
      // an edited idea must not show idea A's asks next to idea B.
      setHints(hintsCacheRef.current.get(text) ?? [])
      return
    }
    const cached = hintsCacheRef.current.get(text)
    if (cached) {
      setHints(cached)
      setThinking(false)
      return
    }
    let active = true
    setThinking(true)
    // The previous idea's shots must never survive into this reveal: if the
    // request hangs and the safety timer unlocks submit, the card degrades
    // to the generic frames instead of showing idea A's shots for idea B.
    setHints([])
    // A slow or hung request must not hold Create post hostage; past this
    // point the reveal degrades to the generic frames and submit unlocks.
    // 3.5s, not 8: past this the reveal degrades to generic frames and submit
    // unlocks. The old 8s window read as a dead button (monkey: "disables
    // Generate ~9s with no signal").
    const safety = window.setTimeout(() => {
      if (active) setThinking(false)
    }, 3500)
    const timer = window.setTimeout(() => {
      let entry = hintsInflightRef.current
      if (!entry || entry.prompt !== text) {
        const promise = apiCall(
          CreationIntentService.creationIntentControllerIngredientHintsV1,
          null,
          {
            prompt: text.slice(0, 300),
          }
        )
          .then(res => {
            const next = (res.hints ?? []).filter(h => typeof h === 'string' && h).slice(0, 3)
            // Cache even a discarded response — a failed submit re-runs this
            // effect with the same prompt and should hit the cache, not the LLM.
            cacheHints(text, next)
            return next
          })
          .catch(err => {
            // Cache the failure too: without this, a failed submit re-locks
            // Create post behind a second spin of the same doomed call.
            cacheHints(text, [])
            throw err
          })
        const created = { prompt: text, promise }
        // Deregister by identity (an older request for the same prompt must
        // not evict a newer entry); two-arg then so this bookkeeping branch
        // never surfaces an unhandled rejection.
        const deregister = () => {
          if (hintsInflightRef.current === created) hintsInflightRef.current = null
        }
        promise.then(deregister, deregister)
        entry = created
        hintsInflightRef.current = created
      }
      entry.promise
        .then(next => {
          if (active) {
            setHints(next)
            setThinking(false)
            if (next.length > 0) {
              track(AnalyticsEvents.Home.HOME_INGREDIENT_HINTS_SHOWN, {
                hint_count: next.length,
                prompt_length: text.length,
              })
            }
          }
        })
        .catch(err => {
          console.log('Ingredient hints unavailable:', err)
          if (active) {
            setHints([])
            setThinking(false)
          }
        })
    }, 900)
    return () => {
      active = false
      clearTimeout(timer)
      clearTimeout(safety)
    }
  }, [prompt, submitting, ingredients.length, readyMode, track, cacheHints])

  const topics = useTopicStore(s => s.topics)
  const dismissTopic = useTopicStore(s => s.dismissTopic)
  const suggestions = useMemo(
    () =>
      topics
        .filter(t => t.status === 'suggested' || t.status === 'picked')
        .sort((a, b) => a.position - b.position),
    [topics]
  )
  // A stream-card pick lands its prompt seed here (editable, combinable with
  // dropped photos — the "Inspire me" door converges on the same intent).
  useEffect(() => {
    if (!streamSeed) return
    // A stream pick mid-adoption wins: stop the ghost's self-typing so it
    // can't keep overwriting the picked card's text.
    if (adoptAnimRef.current) {
      clearInterval(adoptAnimRef.current)
      adoptAnimRef.current = null
    }
    const seededText = streamSeed.text.slice(0, MAX_PROMPT_LENGTH)
    setPrompt(seededText)
    seededRef.current = { text: seededText, source: 'stream' }
    textareaRef.current?.focus()
  }, [streamSeed])

  // The thinking moment narrates progress; stops on the last line rather
  // than looping (it reads as work, not a spinner).
  const [thinkingLine, setThinkingLine] = useState(0)
  useEffect(() => {
    if (!thinking) return
    setThinkingLine(0)
    const t = window.setInterval(
      () => setThinkingLine(i => Math.min(i + 1, THINKING_LINES.length - 1)),
      1300
    )
    return () => clearInterval(t)
  }, [thinking])

  // Adopting the ghost turns the demo into the draft: the idea TYPES ITSELF
  // into the field (~400ms) instead of teleporting in, then stays editable,
  // attributed as a chip pick. Any real keystroke cancels the animation.
  useEffect(
    () => () => {
      if (adoptAnimRef.current) clearInterval(adoptAnimRef.current)
    },
    []
  )
  const adoptPick = useCallback(
    (pick: GhostPick) => {
      const full = pick.text.slice(0, MAX_PROMPT_LENGTH)
      seededRef.current = { text: full, source: 'chip', topicId: pick.topicId ?? undefined }
      textareaRef.current?.focus()
      if (adoptAnimRef.current) clearInterval(adoptAnimRef.current)
      track(AnalyticsEvents.Home.HOME_CREATION_CHIP_TAPPED, {
        topic_id: pick.topicId,
        position: pick.idx,
      })
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setPrompt(full)
        return
      }
      let shown = 0
      const step = Math.max(1, Math.ceil(full.length / 24))
      adoptAnimRef.current = window.setInterval(() => {
        shown = Math.min(full.length, shown + step)
        setPrompt(full.slice(0, shown))
        if (shown >= full.length && adoptAnimRef.current) {
          clearInterval(adoptAnimRef.current)
          adoptAnimRef.current = null
        }
      }, 16)
    },
    [track]
  )

  // The prompt is the hero: it grows with the idea instead of scrolling
  // inside a fixed two-line box.
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    const grow = () => {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 220)}px`
    }
    grow()
    // Width changes re-wrap the text without a keystroke.
    window.addEventListener('resize', grow)
    return () => window.removeEventListener('resize', grow)
  }, [prompt])

  // Object URLs leak without an explicit revoke; release them on unmount.
  const ingredientsRef = useRef<Ingredient[]>([])
  ingredientsRef.current = ingredients
  useEffect(
    () => () => {
      for (const ing of ingredientsRef.current) URL.revokeObjectURL(ing.previewUrl)
    },
    []
  )

  const uploadIngredient = useCallback(async (localId: string, file: File) => {
    try {
      const attachment = await apiCall(MediaService.mediaControllerUploadFileV1, null, {
        file,
        id: ulid(),
      })
      setIngredients(prev =>
        prev.map(i =>
          i.localId === localId ? { ...i, status: 'ready', attachmentId: attachment.id } : i
        )
      )
    } catch (err) {
      console.error('Ingredient upload failed:', err)
      setIngredients(prev => prev.map(i => (i.localId === localId ? { ...i, status: 'error' } : i)))
    }
  }, [])

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const all = Array.from(files)
      if (!all.length) return
      const media = all.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'))
      if (!media.length) {
        // The drop zone actively invited the drop — never swallow it silently
        // (HEIC with a blank MIME type, PDFs all land here).
        toast.error('Photos and videos only (JPG, PNG, WebP, MP4, MOV).')
        return
      }
      const sized = media.filter(f => {
        const isVideo = f.type.startsWith('video/')
        const cap = isVideo ? MAX_VIDEO_BYTES : MAX_INGREDIENT_BYTES
        if (f.size > cap) {
          toast.error(`${f.name} is over ${isVideo ? '50MB' : '15MB'}.`)
          return false
        }
        return true
      })
      const room = MAX_INGREDIENTS - ingredientsRef.current.length
      if (room <= 0 || sized.length > room)
        toast.error(`Up to ${MAX_INGREDIENTS} photos and videos per post.`)
      for (const file of sized.slice(0, Math.max(room, 0))) {
        const localId = ulid()
        setIngredients(prev => [
          ...prev,
          {
            localId,
            file,
            previewUrl: URL.createObjectURL(file),
            kind: file.type.startsWith('video/') ? 'video' : 'image',
            status: 'uploading',
          },
        ])
        void uploadIngredient(localId, file)
      }
    },
    [uploadIngredient]
  )

  const removeIngredient = useCallback((localId: string) => {
    if (submittingRef.current) return
    setIngredients(prev => {
      const target = prev.find(i => i.localId === localId)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter(i => i.localId !== localId)
    })
  }, [])

  const retryIngredient = useCallback(
    (localId: string) => {
      if (submittingRef.current) return
      const target = ingredientsRef.current.find(i => i.localId === localId)
      if (!target) return
      setIngredients(prev =>
        prev.map(i => (i.localId === localId ? { ...i, status: 'uploading' } : i))
      )
      void uploadIngredient(localId, target.file)
    },
    [uploadIngredient]
  )

  const readyIngredients = ingredients.filter(i => i.status === 'ready')
  // Any unresolved chip (uploading OR errored) blocks submit: submitting past a
  // failed chip would silently drop a photo the user explicitly added. They
  // resolve it by retrying or removing the chip. handleSubmit re-checks the
  // same gates from refs so Enter can't slip past the disabled button.
  const unresolvedCount = ingredients.filter(i => i.status !== 'ready').length
  const canSubmit =
    !submitting &&
    !thinking &&
    unresolvedCount === 0 &&
    (prompt.trim().length > 0 || readyIngredients.length > 0)
  // The as-is handoff has fewer gates than generation (no thinking pass, no
  // prompt requirement), but a still-uploading or failed file must not slip
  // into the modal, and it needs at least one resolved upload to hand off.
  const canPostAsIs = !submitting && unresolvedCount === 0 && readyIngredients.length > 0

  const handleSubmit = useCallback(async () => {
    // Mid-adoption the prompt holds a half-typed idea; complete it instead
    // of submitting the truncation (the Enter path does the same).
    if (adoptAnimRef.current) {
      clearInterval(adoptAnimRef.current)
      adoptAnimRef.current = null
      if (seededRef.current) setPrompt(seededRef.current.text)
      return
    }
    const text = prompt.trim()
    const attachmentIds = ingredientsRef.current
      .filter(i => i.status === 'ready' && i.attachmentId)
      .map(i => i.attachmentId!)
    // Guard on the ref, not the `submitting` state: two synchronous clicks
    // (a real double-click) both fire before React commits setSubmitting(true),
    // so a state check lets the second one through and mints a duplicate post.
    // The ref flips synchronously here, so the second call bails immediately.
    if ((!text && !attachmentIds.length) || submittingRef.current) return
    if (thinkingRef.current) return
    if (ingredientsRef.current.some(i => i.status !== 'ready')) return
    const seeded = seededRef.current
    const source = !text
      ? 'ingredients'
      : seeded && seeded.text === prompt
        ? seeded.source
        : 'hero_prompt'
    submittingRef.current = true
    setSubmitting(true)
    track(AnalyticsEvents.Home.HOME_CREATION_INTENT_SUBMITTED, {
      source,
      prompt_length: text.length,
      ingredient_count: attachmentIds.length,
    })
    try {
      const response = await apiCall(CreationIntentService.creationIntentControllerCreateV1, null, {
        ...(text ? { prompt: text } : {}),
        ...(attachmentIds.length ? { attachmentIds } : {}),
        ...(shapeOverride ? { shape: shapeOverride } : {}),
        source,
      })
      track(AnalyticsEvents.Home.HOME_CREATION_INTENT_CREATED, {
        project_id: response.projectId,
        shape: response.shape,
        source,
        ingredient_count: attachmentIds.length,
      })
      // A used idea is consumed: dismissing the topic auto-fills a fresh
      // replacement, so the ghost rotation moves on instead of repeating.
      if (source === 'chip' && seeded?.topicId) {
        void dismissTopic(seeded.topicId).catch(err =>
          console.error('Topic consume after intent submit failed:', err)
        )
      }
      navigate(`/post/${response.projectId}`)
    } catch (err) {
      console.error('Creation intent failed:', err)
      track(AnalyticsEvents.Home.HOME_CREATION_INTENT_FAILED, { source })
      toast.error("We couldn't start this post. Your idea is still here, try again.")
      submittingRef.current = false
      setSubmitting(false)
    }
  }, [prompt, track, navigate, dismissTopic, shapeOverride])

  // ── Bring-your-own-content: create directly, land on the arrival modal ───
  // "Post as-is" IS the create: the post is born as a draft with the shelf's
  // media untouched and the typed caption, then the arrival PostModal owns
  // platforms + schedule. No upload modal in between (fold decision).
  const handlePostAsIs = useCallback(async () => {
    if (submittingRef.current) return
    if (ingredientsRef.current.some(i => i.status !== 'ready')) return
    const ready = ingredientsRef.current.filter(i => i.status === 'ready' && i.attachmentId)
    if (ready.length === 0) return
    // The shelf accepts mixes the BYO rules don't (video+images, extra
    // videos, non-BYO mimes) — surface the rule inline instead of a 400.
    const mixError = byoMixError(ready.map(i => i.file.type))
    if (mixError) {
      setByoError(mixError)
      return
    }
    setSubmitting(true)
    const caption = prompt.trim()
    try {
      const res = await apiCall(CreativeService.creativeControllerCreateFromUploadV1, null, {
        attachmentIds: ready.map(i => i.attachmentId!),
        caption: caption || undefined,
        // No platforms and no schedule: the server picks the default primary,
        // and the arrival PostModal owns fan-out + time.
      })
      const platforms = res.creatives.map(c => c.type) as LivePlatform[]
      track(AnalyticsEvents.Posts.BYO_POST_CREATED, {
        source: 'home',
        media_kind: byoMediaKind(ready.map(i => i.file.type)),
        platforms,
        platform_count: platforms.length,
        scheduled: false,
        has_caption: caption.length > 0,
      } satisfies ByoPostCreatedProperties)
      navigate(`/post/${res.project.id}`, { state: { entry_source: 'byo_upload' } })
    } catch (err) {
      console.error('BYO create-from-upload failed:', err)
      setByoError(extractApiError(err, "We couldn't create this post. Please try again."))
      setSubmitting(false)
    }
  }, [prompt, track, navigate])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Tab adopts ONLY while ghost text is visibly on stage: keyboard users
      // get pass-through windows (between ideas) to leave the field, and a
      // blank-gap Tab can't adopt an idea that was never shown. The overlay
      // owns that judgment; a null pick means Tab behaves normally.
      // Ready mode suppresses the ghost, but its last typed idea survives in
      // the overlay's state; the !readyMode guard keeps Tab from adopting an
      // idea that isn't on screen.
      if (e.key === 'Tab' && !e.shiftKey && !prompt && !readyMode) {
        const pick = ghostRef.current?.current() ?? null
        if (pick) {
          e.preventDefault()
          adoptPick(pick)
          return
        }
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        // Enter mirrors whatever the CTA does: in ready mode that's the
        // direct as-is create, never a generation intent.
        if (readyMode) {
          if (canPostAsIs) void handlePostAsIs()
          return
        }
        // handleSubmit completes a mid-flight adoption instead of
        // submitting half an idea, for Enter and the CTA click alike.
        void handleSubmit()
      }
    },
    [handleSubmit, handlePostAsIs, adoptPick, prompt, readyMode, canPostAsIs]
  )

  // ONE label per room (Matan, round 10c): a CTA that rewrites itself with
  // the card's state reads as two different buttons.
  const ctaLabel = 'Generate my post'
  const promptLong = prompt.trim().length >= MIN_HINT_PROMPT_LENGTH
  // Hints stay on stage once photos arrive: each photo lands INSIDE the frame
  // it answers instead of collapsing the ask into an anonymous chip tray.
  const hintsActive = promptLong && hints.length > 0
  // The shelf's idle state is the ingredient tray (blank frames + one line),
  // not the dashed placeholder rectangle the richer states still use. The
  // Post-your-own room never idles: its empty state is the dropzone invite.
  const idleShelf = !readyMode && ingredients.length === 0 && !thinking && !promptLong
  const frameHints = hints.slice(0, isMobile ? 2 : 3)
  // "Got them" is only true when every slot's photo actually resolved; an
  // errored chip must not sit under victory copy beside a disabled CTA.
  const slotsFilled = ingredients.length >= frameHints.length && unresolvedCount === 0
  const hasClip = ingredients.some(i => i.kind === 'video')
  // As-is posts take their format FROM the media (1 video → Reel, 1 image →
  // Image, 2+ → Carousel); ready mode shows it locked on the shape slider.
  const byoShape: IntentShape = hasClip
    ? 'short_vertical_video'
    : ingredients.length >= 2
      ? 'carousel'
      : 'static_image'
  // The declared format retells the empty dropzone and narrows the picker.
  const byoInvite = byoInviteFor(byoChoice)
  const byoAcceptAttr = byoAcceptAttrFor(byoChoice)
  // One skeleton, two voices (Matan, round 8): tile size is set by the
  // ingredients sub-state (labeled slots = large frames, plain tray = small
  // chips) and HOLDS when the media-use toggle flips, so the photos on the
  // shelf never resize mid-transition.
  const largeTiles = hintsActive
  // The plain add tile: always there in ready mode (adding more media to an
  // as-is post is legal), and in ingredients mode once the asks are answered
  // (slots filled) or were never made (no hints).
  const showAddTile =
    ingredients.length < MAX_INGREDIENTS && (readyMode || !hintsActive || slotsFilled)

  // One photo chip, two homes: large inside a labeled frame slot, small in
  // the overflow row / no-hints tray. Remove/retry/upload states identical.
  const renderPhotoFrame = (ing: Ingredient, large: boolean) => (
    <div
      key={ing.localId}
      className={cn(
        'group/frame relative aspect-[4/5] overflow-hidden rounded-[8px] border',
        'animate-in duration-200 zoom-in-95 fade-in motion-reduce:animate-none',
        large ? (isMobile ? 'w-[84px]' : 'w-[92px]') : 'w-14',
        ing.status === 'error' ? 'border-destructive' : large ? 'border-acid/50' : 'border-line'
      )}
    >
      {ing.kind === 'video' ? (
        <video
          src={ing.previewUrl}
          muted
          playsInline
          preload="metadata"
          className={cn('h-full w-full object-cover', ing.status !== 'ready' && 'opacity-50')}
        />
      ) : (
        <img
          src={ing.previewUrl}
          alt=""
          className={cn('h-full w-full object-cover', ing.status !== 'ready' && 'opacity-50')}
          draggable={false}
        />
      )}
      {ing.kind === 'video' && (
        <span className="absolute bottom-0.5 left-0.5 rounded-[4px] bg-bg/70 p-0.5 text-paper">
          <Video size={10} />
        </span>
      )}
      {ing.status === 'uploading' && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Loader2 size={16} className="animate-spin text-paper" />
        </span>
      )}
      {ing.status === 'error' && (
        <button
          type="button"
          onClick={() => retryIngredient(ing.localId)}
          aria-label="Retry upload"
          className="absolute inset-0 flex items-center justify-center bg-black/40"
        >
          <RotateCcw size={16} className="text-paper" />
        </button>
      )}
      <button
        type="button"
        onClick={() => removeIngredient(ing.localId)}
        aria-label="Remove photo"
        className="absolute top-0.5 right-0.5 rounded-full bg-bg p-0.5 text-paper opacity-0 transition-opacity group-hover/frame:opacity-100"
      >
        <X size={12} />
      </button>
    </div>
  )

  // THE INTENT QUESTION, ASKED UPFRONT: the two rooms of the card, visible
  // from page load at the card's top edge (Matan, round 10 — asking on the
  // dropped media was too difficult and unexpected). The BYO create only
  // happens at the Post as-is CTA; switching rooms never opens anything.
  const heroModeToggle = (
    <HeroModeToggle
      value={heroMode}
      disabled={submitting}
      isMobile={isMobile}
      onChange={next => {
        setHeroMode(next)
        setByoError(null)
        // The BYO door "opens" the moment the user steps into the room —
        // there is no modal to mount anymore.
        if (next === 'own') {
          track(AnalyticsEvents.Posts.BYO_UPLOAD_OPENED, {
            source: 'home',
          } satisfies ByoUploadOpenedProperties)
        }
      }}
    />
  )

  return (
    <section className="relative mx-auto w-full max-w-2xl">
      {/* Ambient acid halo: the creation moment glows without shouting. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-x-16 -top-10 h-56 bg-[radial-gradient(55%_60%_at_50%_35%,rgba(198,255,61,0.10),transparent_70%)]"
      />
      <h1 className="relative text-center text-[30px] font-semibold tracking-tight text-paper md:text-[40px]">
        What should we <span className="text-acid">post next?</span>
      </h1>
      <p className="relative mt-2 text-center text-[14px] text-paper-dim">
        Tell Taka your idea, or drop in photos or videos from this week. It comes back as a finished
        post.
      </p>

      <div
        className={cn(
          'relative mt-6 rounded-[16px] border border-line-bright bg-bg-2/90 backdrop-blur-sm transition-all',
          'focus-within:border-acid/70 focus-within:shadow-[0_0_0_1px_rgba(198,255,61,0.25),0_12px_48px_-16px_rgba(198,255,61,0.35)]',
          dragOver && 'border-acid/80 bg-bg-3 shadow-[0_0_0_1px_rgba(198,255,61,0.3)]'
        )}
        onDragEnter={e => {
          e.preventDefault()
          dragDepthRef.current += 1
          setDragOver(true)
        }}
        onDragOver={e => e.preventDefault()}
        onDragLeave={() => {
          dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
          if (dragDepthRef.current === 0) setDragOver(false)
        }}
        onDrop={e => {
          e.preventDefault()
          dragDepthRef.current = 0
          setDragOver(false)
          if (!submitting) addFiles(e.dataTransfer.files)
        }}
      >
        {/* THE TWO ROOMS, named at the door: Create with Taka / Post your
            own. Always visible so the as-is capability advertises itself
            before any media lands (Matan, round 10). */}
        <div className="flex justify-end px-4 pt-3.5">{heroModeToggle}</div>

        {/* THE PROMPT IS THE HERO: big type, grows with the idea. The ghost
            ideas type in an overlay (not the native placeholder) so they can
            DISSOLVE out and carry the "Use this idea" pill inline, attached
            to the very text it adopts. */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={e => {
              // A real keystroke takes over from the self-typing adoption.
              if (adoptAnimRef.current) {
                clearInterval(adoptAnimRef.current)
                adoptAnimRef.current = null
              }
              setPrompt(e.target.value.slice(0, MAX_PROMPT_LENGTH))
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              dragOver
                ? 'Drop them in. These become the post.'
                : readyMode
                  ? 'Write your caption'
                  : undefined
            }
            rows={1}
            disabled={submitting}
            aria-label={readyMode ? 'Write your caption' : 'What do you want to post about?'}
            className="min-h-[58px] w-full resize-none bg-transparent px-5 pt-5 pb-1 text-[18px] leading-snug font-medium text-paper outline-none placeholder:text-paper-faint disabled:opacity-60 md:min-h-[64px] md:text-[20px]"
          />
          <GhostOverlay
            ref={ghostRef}
            // Ghost ideas are GENERATION prompts; in ready mode the field is
            // a caption, so the typewriter yields to the static placeholder
            // instead of pitching ideas the mode can't use.
            active={!prompt && !dragOver && !readyMode}
            suggestions={suggestions}
            submitting={submitting}
            isMobile={isMobile}
            onAdopt={adoptPick}
          />
        </div>

        {/* THE SHELF — a real drop zone, revealed in acts. Idle: one quiet
            line. Idea typed: Taka visibly picks the shots (Create post waits
            for the ask), then the titled shots card lands with the specific
            frames. With ingredients or hints: ONE shared card whose voice
            follows the media-use toggle (Ingredients pitch vs as-is promise). */}
        <div
          className={cn(
            'group/shelf mx-3 mb-2.5 rounded-[10px] border transition-colors',
            // Idle: the wrapper disappears and the compact PILL inside carries
            // the tray look (Matan, round 6: don't spend the whole width).
            // Drag treatment moves onto the pill in idle for the same reason.
            idleShelf
              ? 'border-transparent'
              : dragOver
                ? 'border-dashed border-acid bg-acid/10'
                : 'border-dashed border-line hover:border-acid/50 hover:bg-bg-3/60',
            // One-time acid ring flash the instant the asked-for shots land.
            hintsActive && !thinking && ingredients.length === 0 && 'hero-reveal-flash',
            submitting && 'pointer-events-none opacity-60'
          )}
        >
          {readyMode && ingredients.length === 0 ? (
            /* THE POST-YOUR-OWN INVITE: the room's empty state is a real
               dropzone, visible the moment the user steps in — the door
               users with finished content arrive looking for (round 10). */
            <button
              type="button"
              disabled={submitting}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Add your finished video or photos. Taka publishes them exactly as uploaded."
              className="flex w-full animate-in cursor-pointer flex-col gap-2 px-3.5 py-3 text-left duration-300 fade-in slide-in-from-bottom-1 motion-reduce:animate-none"
            >
              <span className="flex w-full items-center gap-1.5" aria-hidden="true">
                <Upload size={13} className="flex-shrink-0 text-acid" />
                <span className="text-[13px] font-medium text-paper">
                  {dragOver ? 'Drop it in.' : byoInvite.title}
                </span>
              </span>
              <span className="block text-[12px] leading-snug text-paper-faint" aria-hidden="true">
                {byoInvite.sub}
              </span>
              <span className="flex gap-2" aria-hidden="true">
                {Array.from({ length: byoInvite.frames }, (_, i) => (
                  <span
                    key={i}
                    className={cn(
                      'flex aspect-[4/5] w-14 flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed border-line transition-all group-hover/shelf:border-acid/50',
                      dragOver && (i === 0 ? DROP_TARGET_CLS : DROP_SIBLING_CLS)
                    )}
                  >
                    {byoChoice === 'short_vertical_video' ? (
                      <Video
                        size={12}
                        className="flex-shrink-0 text-paper-faint transition-colors group-hover/shelf:text-acid"
                      />
                    ) : (
                      <Plus
                        size={12}
                        className="flex-shrink-0 text-paper-faint transition-colors group-hover/shelf:text-acid"
                      />
                    )}
                  </span>
                ))}
              </span>
            </button>
          ) : ingredients.length === 0 && thinking ? (
            /* THE THINKING MOMENT: no scaffolding, just magic. A hand-placed
               constellation of acid stars breathes around one shimmering line
               while Taka reads the idea (Matan, round 8: "a chance to do
               something that is simply beautiful"). The frames only exist
               once Taka has something to put in them. */
            <div
              className="relative flex min-h-[88px] animate-in items-center overflow-hidden px-4 py-3 duration-200 fade-in motion-reduce:animate-none"
              role="status"
              aria-label="Taka is picking the shots for this idea"
            >
              <span
                aria-hidden="true"
                className="hero-breath pointer-events-none absolute"
                style={{
                  top: '-60%',
                  left: '52%',
                  width: '46%',
                  height: '220%',
                  background: 'radial-gradient(ellipse, rgba(198,255,61,0.14), transparent 68%)',
                }}
              />
              <span aria-hidden="true" className="pointer-events-none absolute inset-0">
                {(isMobile ? THINKING_TWINKLES.slice(0, 5) : THINKING_TWINKLES).map((t, i) => (
                  <svg
                    key={i}
                    viewBox="0 0 24 24"
                    width={t.s}
                    height={t.s}
                    fill="currentColor"
                    className="hero-twinkle absolute text-acid"
                    style={{
                      top: `${t.top}%`,
                      left: `${t.left}%`,
                      ['--tw-delay' as string]: `${t.d}ms`,
                      ['--tw-dur' as string]: `${t.dur}ms`,
                    }}
                  >
                    <path d="M12 0C12.8 7.2 16.8 11.2 24 12 16.8 12.8 12.8 16.8 12 24 11.2 16.8 7.2 12.8 0 12 7.2 11.2 11.2 7.2 12 0Z" />
                  </svg>
                ))}
              </span>
              <span className="shimmer-text relative text-[13.5px] leading-snug">
                {THINKING_LINES[thinkingLine]}
              </span>
            </div>
          ) : hintsActive || ingredients.length > 0 ? (
            /* ONE SKELETON, TWO VOICES (Matan, round 8: flipping the media-use
               toggle must never read as data loss). Ingredients and Ready
               render the SAME card: header row (title left, toggle + Browse
               right, identical geometry), sub line, media row. Only the voice
               swaps, and the swapping pieces cross-fade (200ms, none under
               reduced motion): the shots pitch becomes the as-is promise, the
               labeled hint tiles yield to one plain add tile. Photo tiles are
               keyed by localId and ALWAYS render first in the media row, so
               they keep DOM identity across the flip (no re-mount, no
               flicker). Every control is a real button (no whole-card button:
               photo frames carry remove/retry). */
            <div className="flex w-full animate-in flex-col gap-2 px-3.5 py-3 duration-300 fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
              {/* HEADER ROW: the toggle's ONE home in the JSX, so its position
                  can never drift between modes again. flex-wrap: on narrow
                  screens the right cluster takes its own line instead of
                  crushing the title. */}
              <span className="flex w-full flex-wrap items-center gap-x-1.5 gap-y-1.5">
                {readyMode ? (
                  <span
                    key="ready-title"
                    className="flex min-w-0 animate-in items-center gap-1.5 duration-200 fade-in motion-reduce:animate-none"
                  >
                    <Upload size={13} className="flex-shrink-0 text-acid" />
                    <span className="text-[13px] font-medium text-paper">
                      Posting these as they are.
                    </span>
                  </span>
                ) : hintsActive ? (
                  <span
                    key="slots-title"
                    className="flex min-w-0 animate-in items-center gap-1.5 duration-200 fade-in motion-reduce:animate-none"
                  >
                    <Sparkles size={13} className="flex-shrink-0 text-acid" />
                    <span className="text-[13px] font-medium text-paper">
                      {dragOver
                        ? 'Drop them in.'
                        : ingredients.length === 0
                          ? 'The shots Taka wants for this post'
                          : slotsFilled
                            ? 'Got them. Your shots lead the post.'
                            : 'Landing right in their frames.'}
                    </span>
                  </span>
                ) : (
                  /* Ingredients on the shelf, no idea yet: the card still
                     speaks (Matan, round 9: a bare tray reads as missing
                     text), naming what this mode DOES with the media. */
                  <span
                    key="tray-title"
                    className="flex min-w-0 animate-in items-center gap-1.5 duration-200 fade-in motion-reduce:animate-none"
                  >
                    <Sparkles size={13} className="flex-shrink-0 text-acid" />
                    <span className="text-[13px] font-medium text-paper">
                      {dragOver ? 'Drop them in.' : 'Taka builds the post around these.'}
                    </span>
                  </span>
                )}
                {/* Adding media stays on the + tile in every state — a
                    separate Browse link was a second door to the same picker
                    (Matan, round 9). The mode switch lives at the card's top
                    edge now, not on the shelf (round 10). */}
              </span>
              {/* SUB LINE: the mode's one-sentence promise. */}
              {readyMode ? (
                <span
                  key="ready-sub"
                  className="block animate-in text-[12px] leading-snug text-paper-faint duration-200 fade-in motion-reduce:animate-none"
                >
                  Taka won't touch them. Write your caption, then pick platforms and time.
                </span>
              ) : hintsActive ? (
                <span
                  key="slots-sub"
                  className={cn(
                    'block animate-in text-[12px] leading-snug text-paper-faint duration-200 fade-in motion-reduce:animate-none',
                    ingredients.length === 0 && 'duration-300 fill-mode-backwards'
                  )}
                  style={ingredients.length === 0 ? { animationDelay: '80ms' } : undefined}
                >
                  {ingredients.length === 0
                    ? "Real photos make it feel authentic. Don't have them? Create anyway and Taka will get close."
                    : slotsFilled
                      ? 'They become the post.'
                      : 'Add the rest if you have them, or create now and Taka fills the gaps.'}
                </span>
              ) : (
                <span
                  key="tray-sub"
                  className="block animate-in text-[12px] leading-snug text-paper-faint duration-200 fade-in motion-reduce:animate-none"
                >
                  Type your idea to steer it, or create now and Taka finds the angle.
                </span>
              )}
              {/* MEDIA ROW: photos first, identical in every mode, then
                  whatever the mode puts around them. */}
              <span className="flex flex-wrap gap-2">
                {ingredients.map(ing => renderPhotoFrame(ing, largeTiles))}
                {/* Ingredients voice: the unfilled labeled frames keep
                    asking. On first reveal they land one after another — Taka
                    placing each shot, not a layout swap; when they return
                    from ready mode they just fade back in. */}
                {!readyMode &&
                  hintsActive &&
                  frameHints.map((hint, i) => {
                    if (i < ingredients.length) return null
                    // Photos fill left-to-right, so during a drag the NEXT
                    // empty frame is the true landing slot — it lights up
                    // hard while its siblings stay softly lit.
                    const isDropTarget = dragOver && i === ingredients.length
                    return (
                      <button
                        key={`hint-${i}`}
                        type="button"
                        disabled={submitting}
                        onClick={() => fileInputRef.current?.click()}
                        aria-label={`Add a photo: ${hint}`}
                        title={hint}
                        style={
                          ingredients.length === 0
                            ? { animationDelay: `${140 + i * 120}ms` }
                            : undefined
                        }
                        className={cn(
                          'flex aspect-[4/5] cursor-pointer flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed border-line transition-all hover:border-acid/60',
                          ingredients.length === 0
                            ? 'animate-in duration-300 fill-mode-backwards zoom-in-95 fade-in slide-in-from-bottom-2 motion-reduce:animate-none'
                            : 'animate-in duration-200 fade-in motion-reduce:animate-none',
                          isMobile ? 'w-[84px] px-1' : 'w-[92px] px-1.5',
                          dragOver && (isDropTarget ? DROP_TARGET_CLS : DROP_SIBLING_CLS)
                        )}
                      >
                        <Plus
                          size={12}
                          className={cn(
                            'flex-shrink-0',
                            isDropTarget ? 'text-acid' : 'text-paper-faint'
                          )}
                        />
                        <span className="line-clamp-4 text-center text-[10.5px] leading-tight text-paper-dim">
                          {hint}
                        </span>
                      </button>
                    )
                  })}
                {/* The plain add tile: ready mode's stand-in for the asks,
                    and the ingredients tray/filled-slots overflow door. Sized
                    to the row's tiles so the card height never jumps. */}
                {showAddTile && (
                  <button
                    key="add-tile"
                    type="button"
                    disabled={submitting}
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Add more photos"
                    className={cn(
                      'flex aspect-[4/5] animate-in cursor-pointer items-center justify-center rounded-[8px] border border-dashed border-line text-paper-faint transition-all duration-200 fade-in hover:border-acid/50 hover:text-acid motion-reduce:animate-none',
                      largeTiles ? (isMobile ? 'w-[84px]' : 'w-[92px]') : 'w-14',
                      dragOver && cn(DROP_TARGET_CLS, 'text-acid')
                    )}
                  >
                    <Plus size={18} />
                  </button>
                )}
              </span>
              {/* The clip-honesty note is about GENERATION (best frame only);
                  as-is publishing keeps the clip's motion, so it would lie in
                  ready mode. */}
              {hasClip && !readyMode && (
                <span className="block text-[11px] leading-snug text-paper-faint">{CLIP_NOTE}</span>
              )}
            </div>
          ) : promptLong ? (
            <button
              type="button"
              disabled={submitting}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Add photos or videos from this week. They become the post."
              className="flex w-full animate-in cursor-pointer flex-col gap-2 px-3.5 py-3 text-left duration-300 fade-in slide-in-from-bottom-1 motion-reduce:animate-none"
            >
              <span className="flex w-full items-center gap-1.5" aria-hidden="true">
                <Sparkles size={13} className="flex-shrink-0 text-acid" />
                <span className="text-[13px] font-medium text-paper">
                  {dragOver ? 'Drop them in.' : 'Got photos for this? Drop them in.'}
                </span>
              </span>
              <span className="block text-[12px] leading-snug text-paper-faint" aria-hidden="true">
                Real photos make it feel authentic. Don't have them? Create anyway and Taka will get
                close.
              </span>
              <span className="flex gap-2" aria-hidden="true">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className={cn(
                      'flex aspect-[4/5] w-14 flex-col items-center justify-center gap-1 rounded-[8px] border border-dashed border-line transition-all group-hover/shelf:border-acid/50',
                      dragOver && (i === 0 ? DROP_TARGET_CLS : DROP_SIBLING_CLS)
                    )}
                  >
                    <Plus
                      size={12}
                      className="flex-shrink-0 text-paper-faint transition-colors group-hover/shelf:text-acid"
                    />
                  </span>
                ))}
              </span>
            </button>
          ) : (
            /* THE INGREDIENT TRAY (idle): a compact content-width pill, not a
               full-width row (Matan, round 6). Blank frames borrow the spark
               blueprints' corner registration marks; a drag straightens the
               tilted frame and turns everything acid. The pill IS the browse
               button; drag-anywhere on the card still routes here. */
            <button
              type="button"
              disabled={submitting}
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'inline-flex max-w-full cursor-pointer items-center gap-2.5 rounded-[10px] border px-3 py-1.5 text-left transition-colors',
                dragOver
                  ? 'border-dashed border-acid bg-acid/10'
                  : 'border-line/70 bg-bg-3/30 hover:border-line-bright hover:bg-bg-3/60'
              )}
            >
              <span aria-hidden="true" className="relative h-8 w-[52px] flex-shrink-0">
                {IDLE_STACK.map((f, i) => (
                  <span
                    key={i}
                    className={cn(
                      'absolute flex items-center justify-center rounded-[5px] border bg-bg-2',
                      // Reduced motion keeps the tilt but skips the transform
                      // animation; the border color still eases.
                      'transition-all duration-200 motion-reduce:transition-colors',
                      f.cls,
                      !dragOver && f.restTilt,
                      // Soft at rest, a little lift on hover; acid belongs to
                      // the drag moment only (round 7: no accent decoration).
                      dragOver
                        ? 'rotate-0 border-acid text-acid'
                        : 'border-paper/20 text-paper-faint group-hover/shelf:border-paper/40 group-hover/shelf:text-paper-dim'
                    )}
                  >
                    {f.icon === 'image' ? <ImageIcon size={13} /> : <Video size={11} />}
                  </span>
                ))}
              </span>
              <span className="min-w-0 truncate text-[13px] text-paper-dim">
                {dragOver ? (
                  'Drop them in.'
                ) : (
                  <>
                    Add photos or clips
                    {/* The payoff tail renders only where it fits on one line;
                        at 390px the imperative stands alone. */}
                    <span className="hidden sm:inline"> to make this post feel real</span>.
                  </>
                )}
              </span>
            </button>
          )}
        </div>

        {/* flex-wrap: on narrow screens the slider takes its own line instead
            of crushing beside the CTA. */}
        <div className="flex flex-wrap items-center justify-end gap-x-2.5 gap-y-1.5 px-3 pb-3">
          {readyMode ? (
            /* The format still shows, but it's the MEDIA's decision here
               (Matan, round 9): the derived shape reads selected, the rest
               disable, and the tooltip explains. No caption-draft button in
               the prompt box (Matan, round 9) — AI caption help lives
               downstream in the Post modal / editor chat. */
            <>
              {/* Always on stage, like the create room (Matan, round 11): it
                  teaches what's possible. Before files it's a real choice
                  (declared intent — retells the dropzone, narrows the
                  picker; re-click deselects); with files the media's derived
                  shape locks the thumb. */}
              <ShapeSlider
                hideAuto
                value={byoChoice}
                lockedShape={ingredients.length > 0 ? byoShape : null}
                isMobile={isMobile}
                onChange={next => setByoChoice(cur => (next === cur ? null : next))}
              />
              {byoError && (
                <span className="text-[11.5px] leading-snug text-[color:var(--alert-error-text)]">
                  {byoError}
                </span>
              )}
            </>
          ) : (
            /* Always on stage so the formats are discoverable before an idea
               exists. Auto resolves to the inferred shape as part of the
               card's thinking beat: the label lands with the shots reveal. */
            <ShapeSlider
              value={shapeOverride}
              inferred={inferShape(prompt, readyIngredients.length)}
              // Same threshold as the shots pass, so the beat reads bare →
              // shimmer → resolve instead of resolving early and un-resolving
              // the moment the prompt gets long enough to think about.
              resolved={promptLong || readyIngredients.length > 0}
              thinking={thinking}
              disabled={submitting}
              isMobile={isMobile}
              onChange={setShapeOverride}
            />
          )}
          <Button
            // Dormant until there's something to create: a full acid CTA on an
            // empty card promises an action that can't happen. The tertiary →
            // primary flip when content lands doubles as the "now it's real"
            // signal (and makes ghost-vs-typed legible at a glance).
            variant={
              prompt.trim().length > 0 || readyIngredients.length > 0 ? 't3-primary' : 't3-tertiary'
            }
            size="t3-sm"
            onClick={() => {
              // In ready mode the CTA IS the create: the post is born with
              // the shelf's media untouched and the typed caption, then the
              // arrival PostModal owns platforms + time.
              if (readyMode) void handlePostAsIs()
              else void handleSubmit()
            }}
            disabled={readyMode ? !canPostAsIs : !canSubmit}
            aria-label={readyMode ? 'Schedule my post' : ctaLabel}
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                {readyMode ? 'Creating…' : 'Starting…'}
              </>
            ) : thinking && !readyMode ? (
              /* The hint pass briefly holds submit — say so instead of a
                 silently dead button. */
              <>
                <Loader2 size={16} className="animate-spin" />
                Thinking…
              </>
            ) : readyMode ? (
              /* First-person, and it names the actual next beat: the arrival
                 modal where platforms and time get picked. */
              <>
                Schedule my post
                <ArrowUp size={16} />
              </>
            ) : (
              <>
                {ctaLabel}
                <ArrowUp size={16} />
              </>
            )}
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          // Post-your-own narrows to the BYO mimes (and further to the
          // declared format); the create room's ingredient shelf stays broad.
          accept={readyMode ? byoAcceptAttr : 'image/*,video/*'}
          multiple
          className="hidden"
          onChange={e => {
            if (e.target.files) addFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </div>
    </section>
  )
}
